import React from "react";

import { includes, some } from "lodash";

import WebsocketClient, { GroupEmitter } from "~/lib/websocket";
import toast from "~/ui/toast";

export const ConnectivityContext = React.createContext({ online: true });
ConnectivityContext.displayName = "ConnectivityContext";

interface ConnectivityProviderProps {
	websocketClient: WebsocketClient;
}

interface ConnectivityProviderState {
	online: boolean;
}

export default class ConnectivityProvider extends React.Component<
	ConnectivityProviderProps,
	ConnectivityProviderState
> {
	state: ConnectivityProviderState = { online: true };

	static Consumer = ConnectivityContext.Consumer;
	static Context = ConnectivityContext;

	offlineToastTimeout = 0;
	onlineToastTimeout = 0;
	gqlSocket: GroupEmitter | null = null;

	onlineListener = () => {
		this.setState({ online: true });
		window.window.clearTimeout(this.offlineToastTimeout);
		this.toastOnline(500);
	};

	offlineListener = () => {
		this.setState({ online: false });
		// This timeout is low because there's already a delay when offline event is fired
		this.toastOffline(toast.isActive("online-notify") ? 500 : 1);
	};

	componentDidMount() {
		this.props.websocketClient.on("online", this.onlineListener);
		this.props.websocketClient.on("offline", this.offlineListener);

		this.gqlSocket = this.props.websocketClient.getOrJoinGroup(`gql.${window.INITIAL_STATE.user?.id}`);
	}

	componentWillUnmount() {
		this.props.websocketClient.off("online", this.onlineListener);
		this.props.websocketClient.off("offline", this.offlineListener);

		this.gqlSocket?.close();
		this.gqlSocket = null;
	}

	toastOnline = (timeout: number) => {
		window.clearTimeout(this.onlineToastTimeout);
		window.clearTimeout(this.offlineToastTimeout);

		if (toast.isActive("offline-notify")) {
			toast.dismiss("offline-notify");

			this.onlineToastTimeout = window.setTimeout(() => {
				toast.success("Fellow has reconnected!", { toastId: "online-notify" });
			}, timeout);
		}
	};

	toastOffline = (timeout: number) => {
		window.clearTimeout(this.onlineToastTimeout);
		window.clearTimeout(this.offlineToastTimeout);

		this.offlineToastTimeout = window.setTimeout(() => {
			toast.dismiss("online-notify");
			if (this.shouldToast()) {
				toast.error("Fellow has lost contact with its servers. Check your connection or you may lose work!", {
					autoClose: false,
					toastId: "offline-notify",
				});
			}
		}, timeout);
	};

	shouldToast = (): boolean => {
		return some(
			[...this.props.websocketClient.groups.keys()].map((groupName: string) => includes(groupName, "notes.data")),
		);
	};

	_lastContextValue?: { online: boolean };
	getContextValue() {
		if (!this._lastContextValue || this._lastContextValue.online !== this.state.online) {
			this._lastContextValue = { online: this.state.online };
		}

		return this._lastContextValue;
	}

	render() {
		return <ConnectivityContext.Provider {...this.props} value={this.getContextValue()} />;
	}
}

ConnectivityProvider.Consumer = ConnectivityContext.Consumer;
ConnectivityProvider.Context = ConnectivityContext;
